import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import classNames from 'classnames'
import { SmsBlockType, SmsStatus } from 'feature/auth/redux/authSlice'
import { useSendCompanyCodeMutation } from 'feature/company/redux/company.api'
import { phones } from 'helpers/phones'
import { FormEvent, useEffect, useState } from 'react'
import { useActions, useAppDispatch } from 'store/hooks'
import { Button, FormControl, InputLabel, OutlinedInput } from 'ui'
import { LoaderInCentalPosition, TextMaskPhone } from 'widget'
import { Severity, addNotification } from 'widget/Notifications'

import style from 'feature/auth/AuthForm.module.scss'

type Props = {
  onBack: () => void
  onCodeSent: () => void
}

export function CompanyPhoneForm({ onBack, onCodeSent }: Props) {
  const [phone, setPhone] = useState('')
  const [hasError, setHasError] = useState(false)
  const [isButtonDisabled, setButtonDisabled] = useState(true)

  const { setUserInfo, setSmsInfo } = useActions()
  const dispatch = useAppDispatch()
  const [sendCompanyCode, { isLoading }] = useSendCompanyCodeMutation()

  const checkPhoneLength = (phoneNumber: string) => {
    const numbersOnly = phones(phoneNumber)
    return numbersOnly?.length !== 11
  }

  const handleChange = (event: { target: { value: string } }) => {
    setPhone(event.target.value)
    setSmsInfo({ blockType: null, blockTime: 0 })
  }

  const handleBlur = () => {
    setHasError(checkPhoneLength(phone))
  }

  useEffect(() => {
    setButtonDisabled(checkPhoneLength(phone))
    setHasError(false)
  }, [phone])

  const submitHandler = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formattedPhone = `+${phones(phone)}`

    try {
      const data = await sendCompanyCode({ phone: formattedPhone }).unwrap()

      setUserInfo({
        phone,
        isDataAgreeRequired: data.signAgreementProcessingPersonalDataRequired,
      })
      sessionStorage.setItem('companyRegistrationPhone', phone)

      if (data.blockedTime !== 0) {
        setSmsInfo({
          error: null,
          blockTime: data.blockedTime,
          blockType: SmsBlockType.countLimit,
        })
      } else {
        setSmsInfo({
          error: null,
          status: SmsStatus.sendedCode,
          blockTime: data.retryAfter,
          blockType: SmsBlockType.timeNotRunout,
        })
        onCodeSent()
      }
    } catch (error: unknown) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'Не удалось отправить код. Попробуйте позже.'
      dispatch(addNotification({ severity: Severity.error, text: message }))
    }
  }

  return (
    <div className={classNames(style.formWrapper, 'mt24')}>
      <form className={style.authForm} noValidate onSubmit={submitHandler}>
        <p className='h1'>Подтверждение телефона</p>
        <p className={classNames(style.subtitle, 'mb24')}>
          Введите номер телефона для подтверждения
        </p>

        <FormControl className={style.authFormControl} error={hasError}>
          <InputLabel htmlFor='company-phone-input'>Номер телефона</InputLabel>
          <OutlinedInput
            label='Номер телефона'
            fullWidth
            type='tel'
            value={phone}
            id='company-phone-input'
            autoFocus
            onChange={handleChange}
            onBlur={handleBlur}
            inputProps={{
              inputprops: { min: 18 },
              'data-testid': 'company-login-phone-input',
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            inputComponent={TextMaskPhone as any}
          />
        </FormControl>

        <Button
          className={style.btn}
          type='submit'
          variant='contained'
          disabled={isButtonDisabled || isLoading}
          endIcon={<ChevronRightIcon />}
          data-testid='company-login-phone-submit'
        >
          {isLoading && <LoaderInCentalPosition className={style.loader} />}
          Получить код
        </Button>
      </form>

      <Button
        type='button'
        variant='contained'
        className={style.backButton}
        color='secondary'
        startIcon={<ChevronLeftIcon color='primary' />}
        onClick={onBack}
        data-testid='company-login-phone-back'
      >
        Назад
      </Button>
    </div>
  )
}
